import { useQueries } from "@tanstack/react-query";
import { client as insightsClient } from "@harmonyso/insights-engine-sdk";
import type { Cell } from "@/lib/cells/cells";
import { cellQueryPolicy } from "@/lib/cells/cell-session";
import { mergeCellTenantRows } from "@/lib/cells/cell-insights";
import { managementTenantId } from "@/lib/cells/management-tenant";

/**
 * Headline numbers for one tenant's estate report — matches its Executive board.
 * `deflection_rate` is a percentage (0-100) and may be null when the tenant has
 * too few workable conversations to compute a rate.
 */
export interface EstateSummary {
  tenant_id: string;
  company: string | null;
  conversations: number | null;
  deflection_rate: number | null;
  deflected: number | null;
  workable: number | null;
  window_days: number | null;
  generated_at: string | null;
}

/**
 * List every tenant that has a generated estate report, with its headline
 * numbers, from each of `cells`.
 *
 * This is the roster for the AI Deflection picker — a tenant appears here exactly
 * when it has a report, so the list and the reports stay aligned with no hard-coded
 * tenant list. Goes through the insights-engine axios instance directly (like
 * useAiDeflectionMap); the backoffice-api proxy requires the cell's management
 * tenant as a `tenant_id` query param, which also routes the call to that cell.
 * Each tenant is listed only from its own cell, using `tenants` to know where
 * each one lives.
 */
export function useEstateSummaries(
  cells: Cell[],
  tenants: { id: string; deployment_cell?: string | null }[] | undefined,
) {
  const homeCells = new Map(
    (tenants ?? []).map((tenant) => [
      tenant.id,
      tenant.deployment_cell ?? undefined,
    ]),
  );
  return useQueries({
    queries: cells.map((cell) => {
      const tenantId = managementTenantId(cell);
      return {
        queryKey: ["estateSummaries", cell.id, tenantId],
        queryFn: async () => {
          const resp = await insightsClient.instance.get<{
            items: EstateSummary[];
          }>("/api/v1/internal/management/estate-summaries", {
            params: { tenant_id: tenantId },
          });
          return resp.data.items;
        },
        staleTime: 5 * 60_000,
        gcTime: 10 * 60_000,
        ...cellQueryPolicy(cell),
      };
    }),
    combine: (results) => {
      const merged = mergeCellTenantRows(
        cells.map((cell, index) => ({
          cell,
          rows: results.at(index)?.data,
          error: results.at(index)?.data ? null : results.at(index)?.error,
          isLoading: results.at(index)?.isPending,
        })),
        (tenantId) => homeCells.get(tenantId),
      );
      return { ...merged, isLoading: merged.loadingCells.length > 0 };
    },
  });
}
